import useSWR from 'swr';
import { getDbStats, cleanupOddsHistory } from '../../api';
import type { DbStats, CleanupStats } from './types/database';
import { useState } from 'react';

interface UseDbStatsReturn {
  stats: DbStats | undefined;
  isLoading: boolean;
  isError: any;
  mutate: () => void;
  cleanupOddsHistory: (dryRun?: boolean) => Promise<CleanupStats>;
  isCleaningUp: boolean;
}

/**
 * Hook to fetch database statistics and perform cleanup operations
 *
 * @returns Database stats, loading state, error state, and cleanup function
 *
 * @example
 * const { stats, isLoading, cleanupOddsHistory } = useDbStats();
 *
 * // Dry run to see what would be deleted
 * const result = await cleanupOddsHistory(true);
 *
 * // Actually delete old records
 * const result = await cleanupOddsHistory(false);
 */
export function useDbStats(): UseDbStatsReturn {
  const [isCleaningUp, setIsCleaningUp] = useState(false);

  const { data, error, mutate } = useSWR<DbStats>(
    '/admin/db-stats',
    getDbStats,
    {
      refreshInterval: 60000, // Refresh every minute
      revalidateOnMount: true,
    }
  );

  const handleCleanup = async (dryRun = true): Promise<CleanupStats> => {
    setIsCleaningUp(true);
    try {
      const result = await cleanupOddsHistory(dryRun);

      // Revalidate stats after cleanup (if not dry run)
      if (!dryRun) {
        await mutate();
      }

      return result;
    } finally {
      setIsCleaningUp(false);
    }
  };

  return {
    stats: data,
    isLoading: !error && !data,
    isError: error,
    mutate,
    cleanupOddsHistory: handleCleanup,
    isCleaningUp,
  };
}
